import {axiosInstance} from './api';
import type {Paginated} from './types';
import type {ZoneWorkingHoursDto} from './workingHours';

export type ZoneType =
  | 'BUILDING' | 'ROOM' | 'FLOOR' | 'HALLWAY' | 'CLASSROOM'
  | 'GYM' | 'COWORKING' | 'CANTEEN' | 'SHOP' | 'COFFEESHOP' | 'RETAIL_BRANCH';

export interface ZoneDto {
  id: string;
  humanreadable_name: string;
  timezone?: string;
  type?: ZoneType;
  is_archived?: boolean;
  // Capture-topology key: matches `LocalComputer.deployment_key` when the
  // branch is served by an on-site computer (jetson/Pi); empty/other for
  // centrally-captured (router/direct) branches. BE ZoneSerializer is
  // fields="__all__" so this is already in every response.
  deployment?: string | null;
  // True when the branch has an enabled Microphone equipment record
  // (BE: ZoneSerializer.get_has_mic). Gates the speech-analytics tab —
  // replaces the old hardcoded zone-name heuristic.
  has_mic?: boolean;
  // Street address as the client wants it shown (review form, maps). Free text.
  address?: string;
  working_hours?: ZoneWorkingHoursDto[];
  // `grafana_dashboard_url`, `checklist_sheet_url`, `telegram_topic_id`
  // were extracted out of `core.Zone` into owning plugins (task #46, BE
  // commit `a841d00`). The Zone serializer no longer returns or accepts
  // them. Grafana URL: `api/grafanaIntegration.ts`. The other two have
  // no live consumers today.
  plan_image?: string | null;
  // Pin-message template fields (cctv_core 0128). All optional —
  // the bot's render skips sections whose URL is empty.
  grafana_folder_url?: string;
  queue_dashboard_url?: string;
  layout_dashboard_url?: string;
  chat_invite_url?: string;
  // Сеть филиала и его страница на сайте сети (cctv_core 0140, Алексей 07.10 #1278).
  // brand — id из /core/api/brands/; brand_plugin — что умеет branch-level плагин
  // сети для ЭТОГО филиала (null — сети нет или у сети нет плагина).
  brand?: number | null;
  website_url?: string;
  /** «Сотрудники должны приходить за N мин» — дефолт зоны (#1324). */
  staff_arrival_minutes?: number;
  /** Сколько сотрудников должно быть на месте одновременно (#1599); 1 по умолчанию. */
  staff_arrival_people?: number;
  brand_plugin?: BrandPluginInfo | null;
}

export interface BrandPluginInfo {
  code: string;
  name: string;
  website: string;
  capabilities: string[];
  qr_style: { identity: string; logo: string; caption_caps: boolean } | null;
}

export type ZonePinTemplateFields = Pick<
  ZoneDto,
  'grafana_folder_url' | 'queue_dashboard_url' | 'layout_dashboard_url' | 'chat_invite_url'
>;

export const listZones = (pageSize = 1000, includeArchived = false) => {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  if (includeArchived) params.set('include_archived', 'true');
  return axiosInstance.get<Paginated<ZoneDto>>(`/core/api/zones/?${params}`);
};

export const createZone = (humanreadable_name: string) =>
  axiosInstance.post<ZoneDto>('/core/api/zones/', { humanreadable_name });

export const updateZone = (
  id: string,
  payload: Partial<
    Pick<ZoneDto, 'humanreadable_name' | 'timezone' | 'type' | 'is_archived' | 'address' | 'brand' | 'website_url' | 'staff_arrival_minutes' | 'staff_arrival_people'>
    & ZonePinTemplateFields
  >,
) => axiosInstance.patch<ZoneDto>(`/core/api/zones/${id}/`, payload);

export const getZone = (id: string) =>
  axiosInstance.get<ZoneDto>(`/core/api/zones/${id}/`);

export const uploadZonePlanImage = (id: string, file: File) => {
  const fd = new FormData();
  fd.append('plan_image', file);
  return axiosInstance.patch<ZoneDto>(`/core/api/zones/${id}/`, fd);
};

export const deleteZonePlanImage = (id: string) =>
  axiosInstance.patch<ZoneDto>(`/core/api/zones/${id}/`, { plan_image: null });
